import { useEffect, useRef } from 'react'

import { CHARACTER_NAMES } from './characterRoster'

export const HUB_TOOL_EVENT = 'hub-tool'

/** Fired when the hub → project explore zoom finishes; ProjectMedia listens to resume playback. */
export const PROJECT_EXPLORE_HANDOFF_EVENT = 'project-explore-handoff'

export const emitProjectExploreHandoff = () => {
  window.dispatchEvent(new CustomEvent(PROJECT_EXPLORE_HANDOFF_EVENT))
}

export const HUB_TOOL_IDS = {
  lights: 'lights',
  animation: 'animation',
  charactersDuoIdle: 'characters-duo-idle',
  charactersDuoDance: 'characters-duo-dance',
  charactersContactIdle: 'characters-contact-idle',
  charactersContactDance: 'characters-contact-dance',
  charactersSolo: 'characters-solo',
  charactersSoloDance: 'characters-solo-dance',
  eyesSakira: 'eyes-sakira',
  eyesSong: 'eyes-song',
  mediaLayout: 'media-layout',
  contactLayout: 'contact-layout',
  themeColors: 'theme-colors',
  themeColorPicker: 'theme-color-picker',
  sceneLoadDebug: 'scene-load-debug',
  showRig: 'show-rig',
  showJoints: 'show-joints',
}

export const OWNER_WINDOW_TOOL_IDS = new Set([
  HUB_TOOL_IDS.charactersDuoIdle,
  HUB_TOOL_IDS.charactersDuoDance,
  HUB_TOOL_IDS.charactersContactIdle,
  HUB_TOOL_IDS.charactersContactDance,
  HUB_TOOL_IDS.charactersSolo,
  HUB_TOOL_IDS.charactersSoloDance,
  HUB_TOOL_IDS.eyesSakira,
  HUB_TOOL_IDS.eyesSong,
  HUB_TOOL_IDS.mediaLayout,
  HUB_TOOL_IDS.contactLayout,
  HUB_TOOL_IDS.sceneLoadDebug,
  HUB_TOOL_IDS.animation,
])

export const RIG_OVERLAY_EVENT = 'rig-overlay'
export const JOINT_OVERLAY_EVENT = 'joint-overlay'

const OVERLAY_CHARACTER_IDS = CHARACTER_NAMES

const createOverlayState = () =>
  Object.fromEntries(OVERLAY_CHARACTER_IDS.map((id) => [id, false]))

let rigOverlayByCharacter = createOverlayState()
let jointOverlayByCharacter = createOverlayState()

const readOverlayMap = (map) => ({ ...map })

const readOverlayForCharacter = (map, characterId) => {
  if (characterId) return Boolean(map[characterId])
  return OVERLAY_CHARACTER_IDS.some((id) => map[id])
}

/** @param {string} [characterId] When omitted, true if ANY character has rig overlay on. */
export const readShowRigOverlay = (characterId) =>
  readOverlayForCharacter(rigOverlayByCharacter, characterId)

/** @param {string} [characterId] When omitted, true if ANY character has joint overlay on. */
export const readShowJointOverlay = (characterId) =>
  readOverlayForCharacter(jointOverlayByCharacter, characterId)

export const readRigOverlayState = () => readOverlayMap(rigOverlayByCharacter)

export const readJointOverlayState = () => readOverlayMap(jointOverlayByCharacter)

const emitCharacterOverlay = (eventName, map, { characterId, show = false } = {}) => {
  if (characterId) {
    if (!OVERLAY_CHARACTER_IDS.includes(characterId)) return
    map[characterId] = Boolean(show)
  } else {
    OVERLAY_CHARACTER_IDS.forEach((id) => {
      map[id] = Boolean(show)
    })
  }

  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail: {
        characters: readOverlayMap(map),
        characterId: characterId || null,
        show: readOverlayForCharacter(map),
      },
    })
  )
}

/** @param {{ characterId?: string, show?: boolean }} options Pass characterId to set one; omit to set all. */
export const emitRigOverlay = ({ characterId, show = false } = {}) => {
  emitCharacterOverlay(RIG_OVERLAY_EVENT, rigOverlayByCharacter, { characterId, show })
}

/** @param {{ characterId?: string, show?: boolean }} options Pass characterId to set one; omit to set all. */
export const emitJointOverlay = ({ characterId, show = false } = {}) => {
  emitCharacterOverlay(JOINT_OVERLAY_EVENT, jointOverlayByCharacter, { characterId, show })
}

export const VIEWPORT_SHADING_EVENT = 'viewport-shading'

/** Base material modes — wireframe overlays independently on top. */
export const VIEWPORT_SHADING_BASE = {
  textured: 'textured',
  smooth: 'smooth',
}

export const VIEWPORT_LIGHTING = {
  normal: 'normal',
  flat: 'flat',
  off: 'off',
}

export const DEFAULT_VIEWPORT_SHADING = {
  base: VIEWPORT_SHADING_BASE.textured,
  wireframeOnly: false,
  wireframeOnShaded: false,
  lightingMode: VIEWPORT_LIGHTING.normal,
  lights: true,
}

let viewportShadingState = { ...DEFAULT_VIEWPORT_SHADING }

export const readViewportShadingState = () => ({ ...viewportShadingState })

export const emitViewportShading = ({
  base,
  wireframeOnly,
  wireframeOnShaded,
  lightingMode,
  lights,
  skipDialogue = false,
} = {}) => {
  const mode =
    lightingMode
    || (lights === false ? VIEWPORT_LIGHTING.off : VIEWPORT_LIGHTING.normal)
  const detail = {
    base: base ?? DEFAULT_VIEWPORT_SHADING.base,
    wireframeOnly: Boolean(wireframeOnly),
    wireframeOnShaded: Boolean(wireframeOnShaded),
    lightingMode: mode,
    lights: mode !== VIEWPORT_LIGHTING.off,
    skipDialogue: Boolean(skipDialogue),
  }
  viewportShadingState = { ...detail }
  window.dispatchEvent(
    new CustomEvent(VIEWPORT_SHADING_EVENT, {
      detail,
    })
  )
}

export const VIEWPORT_HUD_STATS_EVENT = 'viewport-hud-stats'

export const EMPTY_VIEWPORT_HUD_STATS = {
  verts: 0,
  edges: 0,
  faces: 0,
  tris: 0,
}

export const emitViewportHudStats = (stats = EMPTY_VIEWPORT_HUD_STATS) => {
  window.dispatchEvent(
    new CustomEvent(VIEWPORT_HUD_STATS_EVENT, {
      detail: {
        verts: Math.round(stats.verts) || 0,
        edges: Math.round(stats.edges) || 0,
        faces: Math.round(stats.faces) || 0,
        tris: Math.round(stats.tris) || 0,
      },
    })
  )
}

export const emitHubTool = (id, action) => {
  window.dispatchEvent(new CustomEvent(HUB_TOOL_EVENT, { detail: { id, action } }))
}

/** Saved hub tuners / layouts — cleared by File → New Scene. */
const HUB_SCENE_STORAGE_KEYS = [
  'hub-character-duo-idle-v2',
  'hub-character-duo-dance-v2',
  'hub-character-contact-duo-idle-v2',
  'hub-character-contact-duo-dance-v2',
  'hub-character-solo-v2',
  'hub-character-solo-dance-v2',
  'hub-character-duo-idle-v1',
  'hub-character-duo-dance-v1',
  'hub-character-contact-duo-idle-v1',
  'hub-character-contact-duo-dance-v1',
  'hub-character-solo-v1',
  'hub-character-pair-v2',
  'hub-lights-v2',
  'hub-animation-v1',
  'hub-flat-media-layout-v2',
  'hub-flat-contact-layout-v2',
  'portfolio-theme-color-overrides-v2',
  'portfolio-theme-color-overrides-v1',
  'portfolio-theme-paint-v1',
]

const clearHubSceneStorage = () => {
  try {
    HUB_SCENE_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Ignore quota / private-mode failures
  }
}

/** Wipe browser-saved hub tool values and reload code defaults in open tuners. */
export const HUB_SCENE_RESET_EVENT = 'hub-scene-reset'

export const startNewHubScene = () => {
  clearHubSceneStorage()
  emitHubTool('all', 'loadDefaults')
  emitViewportShading({ ...DEFAULT_VIEWPORT_SHADING })
  emitRigOverlay({ show: false })
  emitJointOverlay({ show: false })
  window.dispatchEvent(new CustomEvent(HUB_SCENE_RESET_EVENT))
}

// V2: dialogue listens here
export const SHOWREEL_WATCH_EVENT = 'showreel-watch'

/** Resets on page refresh — only tracks within the current visit. */
let showreelWatchedThisVisit = false

try {
  sessionStorage.removeItem('portfolio-showreel-watched')
} catch {
  /* ignore */
}

/** True after the viewer has started the showreel once this visit (same page load). */
export const hasWatchedShowreelBefore = () => showreelWatchedThisVisit

export const markShowreelWatched = () => {
  showreelWatchedThisVisit = true
}

/** @param {{ watching?: boolean, repeat?: boolean }} detail */
export const emitShowreelWatch = ({ watching = true, repeat = false } = {}) => {
  window.dispatchEvent(
    new CustomEvent(SHOWREEL_WATCH_EVENT, {
      detail: { watching: Boolean(watching), repeat: Boolean(repeat) },
    }),
  )
}

// V2: dialogue listens here
export const MUSIC_PLAYBACK_EVENT = 'music-playback'

/** @param {{ playing?: boolean, title?: string, artist?: string, trackIndex?: number }} detail */
export const emitMusicPlayback = ({
  playing = false,
  title = '',
  artist = '',
  trackIndex = 0,
} = {}) => {
  window.dispatchEvent(
    new CustomEvent(MUSIC_PLAYBACK_EVENT, {
      detail: {
        playing: Boolean(playing),
        title: String(title),
        artist: String(artist),
        trackIndex: Number(trackIndex) || 0,
      },
    }),
  )
}

export const DIALOGUE_TOGGLE_EVENT = 'dialogue-toggle'
const DIALOGUE_STORAGE_KEY = 'portfolio-dialogue-v1'

export const readDialogueEnabled = () => {
  try {
    const raw = window.localStorage.getItem(DIALOGUE_STORAGE_KEY)
    if (raw === null) return true
    return raw !== 'false'
  } catch {
    return true
  }
}

export const setDialogueEnabled = (next) => {
  const enabled = Boolean(next)
  try {
    window.localStorage.setItem(DIALOGUE_STORAGE_KEY, enabled ? 'true' : 'false')
  } catch {
    // Ignore quota / private-mode failures
  }
  window.dispatchEvent(
    new CustomEvent(DIALOGUE_TOGGLE_EVENT, {
      detail: { enabled },
    })
  )
}

export const CONTACT_FORM_EVENT = 'contact-form'

/** @param {{ outcome?: string }} detail */
export const emitContactForm = ({ outcome } = {}) => {
  if (!outcome) return
  window.dispatchEvent(
    new CustomEvent(CONTACT_FORM_EVENT, {
      detail: { outcome: String(outcome) },
    }),
  )
}

export const CHARACTER_REACTION_EVENT = 'character-reaction'

/** @param {'clap' | 'headShake'} kind */
export const emitCharacterReaction = (kind) => {
  if (kind !== 'clap' && kind !== 'headShake') return
  window.dispatchEvent(
    new CustomEvent(CHARACTER_REACTION_EVENT, {
      detail: { kind },
    }),
  )
}

export const useHubToolEvents = (toolId, handlers) => {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!toolId) return undefined

    const handleEvent = (event) => {
      const { id, action } = event.detail || {}
      if (id !== toolId && id !== 'all') return
      if (action === 'open' && id === 'all') return
      handlersRef.current?.[action]?.()
    }

    window.addEventListener(HUB_TOOL_EVENT, handleEvent)
    return () => window.removeEventListener(HUB_TOOL_EVENT, handleEvent)
  }, [toolId])
}

/**
 * Owner Copy clipboard text — ready to paste into source.
 * JS defaults: export const <target> = { …values… }
 * Theme CSS: pass `body` (e.g. colorsToCssBlock) instead of values.
 *
 * @param {{
 *   tool: string,
 *   profile: string,
 *   target: string,
 *   values?: Record<string, unknown>,
 *   body?: string,
 * }} opts
 */
export const formatCopySnippet = ({ tool, profile, target, values, body }) => {
  const now = new Date()
  const date = now.toLocaleDateString()
  const time = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const header = [
    `// ${tool} — ${profile}   (${date} ${time})`,
    `// paste into: ${target}`,
  ].join('\n')

  if (typeof body === 'string') {
    return `${header}\n${body.replace(/\s+$/, '')}\n`
  }

  const objectLiteral = JSON.stringify(values ?? {}, null, 2)
  return `${header}\nexport const ${target} = ${objectLiteral}\n`
}

